import React, { useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Grid, Box, Button, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { QUOTE_FIELD_SYMBOLS } from "../pages/Products/EditItem/Constants";

type CompareField = { label: string; name: string; newKey?: string };
type SecondaryCompareRow = {
  label: string;
  oldKey: string;
  newKey: string;
  oldSymbolKey?: string;
  newSymbolKey?: string;
};

type QuoteDetailsComparisonModalProps = {
  open: boolean;
  onClose: () => void;
  oldValues: Record<string, unknown>;
  newValues: Record<string, unknown>;
  changedFieldsForCalculation?: string[];
  handleSaveProduct: () => void;
  isSubmitting?: boolean;
  /** Quote acceptance flow only — EditProduct omits this and uses legacy behavior */
  mode?: "default" | "quoteAcceptance";
  primaryCompareFields?: CompareField[];
  secondaryCompareRows?: SecondaryCompareRow[];
  firstGridMessage?: string;
  secondGridMessage?: string;
  confirmMessage?: string;
  confirmYesLabel?: string;
  confirmNoLabel?: string;
  handleConfirmNo?: () => void;
};

const EDIT_PRODUCT_COMPARE_FIELDS: CompareField[] = [
  { label: "Brand Collection", name: "brandCollectionDisplayText" },
  { label: "DN Price", name: "dnPrice" },
  { label: "Vendor Cost", name: "itemVendorCost" },
  { label: "IP Cube", name: "ipCube" },
  { label: "Gross GM Dollars", name: "grossGMDollars" },
  { label: "Gross GM Percent", name: "grossGMPercent" },
  { label: "Net Margin", name: "netMargin" },
  { label: "Net Margin Dollars", name: "netMarginDollars" },
];

const QuoteDetailsComparisonModal = ({
  open,
  onClose,
  oldValues,
  newValues,
  changedFieldsForCalculation = [],
  handleSaveProduct,
  isSubmitting = false,
  mode = "default",
  primaryCompareFields,
  secondaryCompareRows,
  firstGridMessage,
  secondGridMessage,
  confirmMessage,
  confirmYesLabel = "Yes",
  confirmNoLabel = "No",
  handleConfirmNo,
}: QuoteDetailsComparisonModalProps) => {
  const hasSavedRef = useRef(false);
  const isQuoteAcceptanceMode = mode === "quoteAcceptance";
  const compareFields = isQuoteAcceptanceMode
    ? (primaryCompareFields ?? [])
    : EDIT_PRODUCT_COMPARE_FIELDS;

  const getValueWithSymbol = (fieldName, fieldValue) => {
    const symbol = QUOTE_FIELD_SYMBOLS[fieldName];
    const value = fieldValue || 0;

    const formattedValue = symbol
      ? symbol === "%"
        ? `${value}${symbol}`
        : `${symbol}${value}`
      : value;

    return formattedValue;
  };

  const getPrimaryNewKey = (field: CompareField) => field.newKey ?? field.name;

  const isQuotePrimaryFieldChanged = (field: CompareField) =>
    newValues?.[getPrimaryNewKey(field)] != oldValues?.[field.name];

  const firstGridFieldsCondition = isQuoteAcceptanceMode
    ? compareFields.some(isQuotePrimaryFieldChanged)
    : changedFieldsForCalculation.filter((name) => !["dnPrice", "itemVendorCost"].includes(name))?.length > 0 &&
      compareFields.filter(
        ({ name }) =>
          !["dnPrice", "itemVendorCost"].includes(name) && newValues?.[name] != oldValues?.[name]
      )?.length > 0;

  const secondGridFieldsCondition = isQuoteAcceptanceMode
    ? isSubmitting &&
      (secondaryCompareRows?.some(({ oldKey, newKey }) => oldValues?.[oldKey] != newValues?.[newKey]) ?? false)
    : isSubmitting &&
      ((oldValues?.dnPrice != newValues?.dnPrice) || (oldValues?.itemVendorCost != newValues?.itemVendorCost)) &&
      compareFields.filter(
        ({ name }) => ["dnPrice", "itemVendorCost"].includes(name) && newValues?.[name] != oldValues?.[name]
      )?.length > 0;

  useEffect(() => {
    if (isQuoteAcceptanceMode && open) {
      hasSavedRef.current = false;
    }
  }, [isQuoteAcceptanceMode, open]);

  useEffect(() => {
    if (isQuoteAcceptanceMode && !open) return;
    if (!(firstGridFieldsCondition || secondGridFieldsCondition) && !hasSavedRef.current) {
      hasSavedRef.current = true;
      handleSaveProduct();
    }
  }, [
    isQuoteAcceptanceMode,
    open,
    firstGridFieldsCondition,
    secondGridFieldsCondition,
    handleSaveProduct,
  ]);

  const renderEditProductFirstGrid = () =>
    compareFields.map(({ label, name }) => {
      if (name !== "dnPrice" && name !== "itemVendorCost" && newValues?.[name] != oldValues?.[name]) {
        return (
          <React.Fragment key={name}>
            <Grid item xs={4} className="grid-item">{label}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(name, oldValues?.[name] || null)}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(name, newValues?.[name] || null)}</Grid>
          </React.Fragment>
        );
      }
    });

  const renderQuoteAcceptanceFirstGrid = () =>
    compareFields.map((field) => {
      const { label, name } = field;
      const newKey = getPrimaryNewKey(field);
      if (newValues?.[newKey] != oldValues?.[name]) {
        return (
          <React.Fragment key={name}>
            <Grid item xs={4} className="grid-item">{label}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(name, oldValues?.[name] || null)}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(newKey, newValues?.[newKey] || null)}</Grid>
          </React.Fragment>
        );
      }
    });

  const renderEditProductSecondGrid = () =>
    compareFields.map(({ label, name }) => {
      if ((name === "dnPrice" || name === "itemVendorCost") && newValues?.[name] != oldValues?.[name]) {
        return (
          <React.Fragment key={name}>
            <Grid item xs={4} className="grid-item">{label}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(name, oldValues?.[name] || null)}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(name, newValues?.[name] || null)}</Grid>
          </React.Fragment>
        );
      }
    });

  const renderQuoteAcceptanceSecondGrid = () =>
    secondaryCompareRows?.map(({ label, oldKey, newKey, oldSymbolKey, newSymbolKey }) => {
      if (oldValues?.[oldKey] != newValues?.[newKey]) {
        return (
          <React.Fragment key={`${oldKey}-${newKey}`}>
            <Grid item xs={4} className="grid-item">{label}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(oldSymbolKey ?? oldKey, oldValues?.[oldKey] || null)}</Grid>
            <Grid item xs={4} className="grid-item">{getValueWithSymbol(newSymbolKey ?? newKey, newValues?.[newKey] || null)}</Grid>
          </React.Fragment>
        );
      }
    });

  const defaultFirstGridMessage =
    "This product has an accepted quote. The changes you made to the product will cause some of the quote field values recalculated. Click save to Proceed";
  const defaultSecondGridMessage =
    "The value you just entered for DN Price or Vendor Cost is driven by the accepted quote. This value will be replaced by the value of the accepted quote.";

  return (
    <>
      {(firstGridFieldsCondition || secondGridFieldsCondition) && (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="quote-details-comparison-modal">
          <Box className="dialog-header">
            <DialogTitle className="dialog-title">
              {isSubmitting
                ? "Confirm! Quote calculated values affected."
                : "Calculated fields are affected by the fields you changed."}
              <IconButton aria-label="close" onClick={onClose} className="close-button">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
          </Box>
          <DialogContent>
            {firstGridFieldsCondition && (
              <>
                <Typography className="warning-message">
                  {isQuoteAcceptanceMode ? firstGridMessage : defaultFirstGridMessage}
                </Typography>
                <Grid container className="grid-header">
                  <Grid item xs={4} className="grid-item">Field</Grid>
                  <Grid item xs={4} className="grid-item">Old Value</Grid>
                  <Grid item xs={4} className="grid-item">New Value</Grid>
                </Grid>
                <Grid container className="grid-content">
                  {isQuoteAcceptanceMode ? renderQuoteAcceptanceFirstGrid() : renderEditProductFirstGrid()}
                </Grid>
              </>
            )}

            {secondGridFieldsCondition && (
              <>
                <Typography className="dnprice warning-message">
                  {isQuoteAcceptanceMode ? secondGridMessage : defaultSecondGridMessage}
                </Typography>
                <Grid container className="grid-header">
                  <Grid item xs={4} className="grid-item">Field</Grid>
                  <Grid item xs={4} className="grid-item">Old Value</Grid>
                  <Grid item xs={4} className="grid-item">New Value</Grid>
                </Grid>
                <Grid container className="grid-content">
                  {isQuoteAcceptanceMode ? renderQuoteAcceptanceSecondGrid() : renderEditProductSecondGrid()}
                </Grid>
              </>
            )}

            {isSubmitting && (
              <Box className="warning-message-container">
                <Typography className="warning-message">
                  {isQuoteAcceptanceMode
                    ? (confirmMessage ?? "Do you want to accept the quote with these changes?")
                    : "Do you want to save the new changes?"}
                </Typography>
                <Box>
                  <Button variant="contained" className="save-btn" onClick={handleSaveProduct}>
                    {isQuoteAcceptanceMode ? confirmYesLabel : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={isQuoteAcceptanceMode ? (handleConfirmNo ?? onClose) : onClose}
                  >
                    {isQuoteAcceptanceMode ? confirmNoLabel : "Cancel"}
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default QuoteDetailsComparisonModal;
